import { useState, useEffect, useCallback } from 'react';

interface PopularityStore {
  counts: Record<number, number>;
  weekStart: string; // ISO date string of the most recent Sunday
}

const STORAGE_KEY = 'article_click_counts';

/** 直近の日曜日（週の開始日）をYYYY-MM-DD形式で返す */
function getMostRecentSunday(date: Date): string {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function loadStore(): PopularityStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PopularityStore;
      return parsed;
    }
  } catch {
    // ignore
  }
  return { counts: {}, weekStart: getMostRecentSunday(new Date()) };
}

function saveStore(store: PopularityStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

/**
 * 記事クリック数を週次でlocalStorageに記録し、
 * 毎週日曜日にリセットするカスタムフック。
 * articleIds: 全記事IDの配列
 * returns { clickCounts, trackClick }
 */
export function useArticlePopularity(articleIds: number[]) {
  const [counts, setCounts] = useState<Record<number, number>>({});

  useEffect(() => {
    const store = loadStore();
    const currentWeekStart = getMostRecentSunday(new Date());

    if (store.weekStart !== currentWeekStart) {
      // 新しい週 → リセット
      const fresh: PopularityStore = { counts: {}, weekStart: currentWeekStart };
      saveStore(fresh);
      setCounts({});
    } else {
      setCounts(store.counts);
    }
  }, []);

  /** 記事IDのクリックを記録 */
  const trackClick = useCallback((articleId: number) => {
    setCounts((prev) => {
      const next = { ...prev, [articleId]: (prev[articleId] ?? 0) + 1 };
      const store = loadStore();
      const updated: PopularityStore = {
        counts: next,
        weekStart: store.weekStart,
      };
      saveStore(updated);
      return next;
    });
  }, []);

  /**
   * articleIdsをクリック数降順でソートし、
   * まだクリックがない記事は元の並び順を維持して末尾に。
   */
  const sortedByPopularity = [...articleIds].sort((a, b) => {
    const ca = counts[a] ?? 0;
    const cb = counts[b] ?? 0;
    return cb - ca;
  });

  return { counts, trackClick, sortedByPopularity };
}
